// client/pages/Limit-order.tsx - Fixed "More Tokens" button under 9 tokens + dark black desktop theme

import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { useTheme } from "@/contexts/ThemeContext";
import { useMultipleTokenPrices } from "@/hooks/use-dexscreener";
import { useSolanaBalances } from "@/hooks/use-solana-balances";
import { ArrowLeft, ArrowUpDown, Loader2, ExternalLink, Clock, History, CheckCircle, X, ChevronDown, AlertTriangle, TrendingUp, TrendingDown, Copy, BarChart3, Info, MoreVertical, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";

// ============ HELPER FUNCTIONS ============
const extractHashFromMessage = (message: string): string => {
  const match = message.match(/([a-zA-Z0-9]{44,})/);
  return match ? match[1] : message;
};

// Format number with appropriate decimals
const formatTokenAmount = (amount: number, symbol: string): string => {
  if (!amount || amount === 0) return "0.000000";
  
  const smallTokens = ["PUMP", "ORCA", "USDC", "USDT", "XRP", "DOGE", "RAY", "stSOL", "mSOL", "FIXERCOIN", "LOCKER", "PINGX", "FXM"];
  
  if (smallTokens.includes(symbol)) {
    return amount.toFixed(9);
  }
  
  if (["SOL", "WBTC", "WETH"].includes(symbol)) {
    return amount.toFixed(6);
  }
  
  return amount.toFixed(6);
};

// Check if token is a stablecoin (USDT or USDC)
const isStablecoin = (symbol: string): boolean => {
  return symbol === "USDT" || symbol === "USDC";
};

// ============ TOKEN CONFIGURATION - ALL SOLANA TOKENS WITH SOL AS BASE ============
const TOKENS = [
  // Native SOL
  { id: "sol_solana", symbol: "SOL", name: "SOLANA", chain: "solana", address: "So11111111111111111111111111111111111111112", isNative: true },
  
  // Major Tokens on Solana
  { id: "usdt_solana", symbol: "USDT", name: "TETHER", chain: "solana", address: "Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB", isStable: true },
  { id: "usdc_solana", symbol: "USDC", name: "USD COIN", chain: "solana", address: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v", isStable: true },
  { id: "wbtc_solana", symbol: "WBTC", name: "WRAPPED BITCOIN", chain: "solana", address: "3NZ9JMVBmGAqocybic2c7LQCJScmgsAZ6vQqTDzcqmJh" },
  { id: "weth_solana", symbol: "WETH", name: "WRAPPED ETHEREUM", chain: "solana", address: "7vfCXTUXx5WJV5JADk17DUJ4ksgau7utNKj4b963voxs" },
  { id: "xrp_solana", symbol: "XRP", name: "RIPPLE", chain: "solana", address: "6UpQcMAb5xMzxc7ZfPaVMgx3KqsvKZdT5U718BzD5We2" },
  { id: "doge_solana", symbol: "DOGE", name: "DOGECOIN", chain: "solana", address: "B9u8h65uM1oifqmP82VyUDb68iG2fKfZiyubmNMtu3h7" },
  
  // Popular Solana Tokens
  { id: "pump_solana", symbol: "PUMP", name: "PUMP", chain: "solana", address: "pumpCmXqMfrsAkQ5r49WcJnRayYRqmXz6ae8H7H9Dfn" },
  { id: "orca_solana", symbol: "ORCA", name: "ORCA", chain: "solana", address: "orcaEKTdK7LKz57vaAYr9QeNsVEPfiu6QeMU1kektZE" },
  { id: "ray_solana", symbol: "RAY", name: "RAYDIUM", chain: "solana", address: "4k3Dyjzvzp8eMZWUXbBCjEvwSkkk59S5iCNLY3QrkX6R" },
  { id: "stsol_solana", symbol: "stSOL", name: "LIDO STAKED SOL", chain: "solana", address: "7dHbWXmci3dT8UFYWYZweBLXgycu7Y3iL6trKn1Y7ARj" },
  { id: "msol_solana", symbol: "mSOL", name: "MARINADE STAKED SOL", chain: "solana", address: "mSoLzYCxHdYgdzU16g5QSh3i5K3z3KZK7ytfqcJm7So" },
  
  // Pump Fun Tokens
  { id: "fixercoin_solana", symbol: "FIXERCOIN", name: "FIXERCOIN", chain: "solana", address: "H4qKn8FMFha8jJuj8xMryMqRhH3h7GjLuxw7TVixpump" },
  { id: "locker_solana", symbol: "LOCKER", name: "LOCKER", chain: "solana", address: "EN1nYrW6375zMPUkpkGyGSEXW8WmAqYu4yhf6xnGpump" },
  { id: "pingx_solana", symbol: "PINGX", name: "PINGX", chain: "solana", address: "7KS4DgKHmgSWYC4uGnSozLUon2bDEj6WKhRNSosmpump" },
  { id: "fxm_solana", symbol: "FXM", name: "FXM", chain: "solana", address: "7Fnx57ztmhdpL1uAGmUY1ziwPG2UDKmG6poB4ibjpump" },
];

// Tokens to show in marquee
const DISPLAY_TOKENS_LIST = ["SOL", "USDT", "USDC", "WBTC", "WETH", "XRP", "DOGE", "ORCA", "RAY", "PUMP", "FIXERCOIN", "LOCKER", "PINGX", "FXM"];

// First 9 tokens to show initially in left column
const INITIAL_DISPLAY_TOKENS = ["SOL", "USDT", "USDC", "WBTC", "WETH", "XRP", "DOGE", "ORCA", "RAY"];

const TOKEN_ADDRESSES: Record<string, string> = {};
TOKENS.forEach(t => {
  TOKEN_ADDRESSES[t.symbol] = t.address;
});

const TOKEN_DECIMALS: Record<string, number> = {
  SOL: 9,
  USDT: 6,
  USDC: 6,
  WBTC: 6,
  WETH: 6,
  XRP: 6,
  DOGE: 6,
  PUMP: 6,
  ORCA: 6,
  RAY: 6,
  stSOL: 9,
  mSOL: 9,
  FIXERCOIN: 6,
  LOCKER: 6,
  PINGX: 6,
  FXM: 6,
};

const CHAIN_NATIVE_TOKENS: Record<string, string> = {
  solana: "SOL",
};

const FALLBACK_PRICES: Record<string, number> = {
  SOL: 180.00,
  USDT: 1.000,
  USDC: 1.000,
  WBTC: 60000.00,
  WETH: 2300.00,
  XRP: 0.50,
  DOGE: 0.15,
  PUMP: 0.000001,
  ORCA: 1.50,
  RAY: 1.20,
  stSOL: 190.00,
  mSOL: 185.00,
  FIXERCOIN: 0.000051,
  LOCKER: 0.000001,
  PINGX: 0.000001,
  FXM: 0.000001,
};

const MIN_SMALLEST_UNITS = 1000;
const getMinimumAmount = (tokenSymbol: string): number => {
  const decimals = TOKEN_DECIMALS[tokenSymbol] || 9;
  return MIN_SMALLEST_UNITS / Math.pow(10, decimals);
};

interface LimitOrder {
  id: string;
  userId: string;
  fromToken: string;
  toToken: string;
  fromAmount: number;
  toAmount: number;
  limitPrice: number;
  orderType: "buy" | "sell";
  status: "pending" | "executed" | "cancelled";
  createdAt: number;
  executedAt?: number;
  cancelledAt?: number;
  chain: string;
}

// ============ BIRDEYE CHART COMPONENT ============
const BirdeyeChart = ({ tokenAddress, chain }: { tokenAddress: string; chain: string }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    if (!tokenAddress || !chartRef.current) return;

    setIsLoading(true);

    const isDark = isDarkMode;
    const theme = isDark ? 'dark' : 'light';
    const backgroundColor = isDark ? '%23000000' : '%23FFFFFF';
    const textColor = isDark ? '%23FFFFFF' : '%23111A28';
    const borderColor = isDark ? '%233F3F46' : '%23E5E7EB';
    const upColor = '%2322C55E';
    const downColor = '%23EF4444';
    
    const widgetUrl = `https://embed.birdeye.so/tv-widget/${tokenAddress}?chain=${chain}&viewMode=pair&chartInterval=1D&chartType=Candle&chartTimezone=Asia%2FKarachi&chartLeftToolbar=hide&theme=${theme}&backgroundColor=${backgroundColor}&textColor=${textColor}&borderColor=${borderColor}&chartOverrides=mainSeriesProperties.candleStyle.downColor=${downColor}&chartOverrides=mainSeriesProperties.candleStyle.borderDownColor=${downColor}&chartOverrides=mainSeriesProperties.candleStyle.wickDownColor=${downColor}&chartOverrides=mainSeriesProperties.candleStyle.upColor=${upColor}&chartOverrides=mainSeriesProperties.candleStyle.borderUpColor=${upColor}&chartOverrides=mainSeriesProperties.candleStyle.wickUpColor=${upColor}`;

    const iframe = document.createElement('iframe');
    iframe.src = widgetUrl;
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = 'none';
    iframe.style.borderRadius = '12px';
    iframe.style.background = 'transparent';
    iframe.allow = 'clipboard-write';
    iframe.onload = () => {
      setIsLoading(false);
    };

    chartRef.current.innerHTML = '';
    chartRef.current.appendChild(iframe);

    return () => {
      if (chartRef.current) {
        chartRef.current.innerHTML = '';
      }
    };
  }, [tokenAddress, chain, isDarkMode]);

  return (
    <div className="bg-card border border-border rounded-xl p-4 h-full flex flex-col shadow-sm">
      <div className="flex justify-between items-center mb-3">
        <div>
          <h3 className="text-[12px] font-bold uppercase tracking-wider text-foreground">
            {TOKENS.find(t => t.address === tokenAddress)?.symbol || 'Token'} / SOL
          </h3>
          <p className="text-[8px] text-muted-foreground uppercase">Birdeye Chart</p>
        </div>
        <div className="flex gap-1">
          <button className="text-[8px] text-muted-foreground hover:text-foreground uppercase px-2 py-0.5 border border-border rounded">1H</button>
          <button className="text-[8px] text-muted-foreground hover:text-foreground uppercase px-2 py-0.5 border border-border rounded">24H</button>
          <button className="text-[8px] text-yellow-500 uppercase px-2 py-0.5 border border-yellow-500 rounded">7D</button>
        </div>
      </div>
      
      <div className="flex-1 relative min-h-[300px]">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted rounded-lg">
            <Loader2 className="h-8 w-8 animate-spin text-yellow-500" />
          </div>
        )}
        <div ref={chartRef} className="w-full h-full rounded-lg overflow-hidden" />
      </div>
    </div>
  );
};

// ============ LEFT COLUMN - TOKEN PRICES (9 tokens visible with "More Tokens" under cards) ============
const TokenPriceList = ({ prices, onSelectToken, selectedToken }: any) => {
  const [showAllTokens, setShowAllTokens] = useState(false);
  
  const formatPrice = (symbol: string, price: number) => {
    if (!price || price === 0) return "$0.00";
    if (symbol === "SOL" || symbol === "WBTC" || symbol === "WETH") {
      return `$${price.toFixed(2)}`;
    }
    if (isStablecoin(symbol)) {
      return `$${price.toFixed(3)}`;
    }
    if (price < 0.00001) return `$${price.toFixed(8)}`;
    if (price < 0.001) return `$${price.toFixed(6)}`;
    return `$${price.toFixed(4)}`;
  };

  const formatChange = (change: number) => {
    if (!change) return "0.00%";
    if (isStablecoin(change)) return "0.00%";
    return `${change >= 0 ? '+' : ''}${change.toFixed(2)}%`;
  };

  const displayTokens = TOKENS.filter(t => DISPLAY_TOKENS_LIST.includes(t.symbol));
  const visibleTokens = showAllTokens ? displayTokens : displayTokens.filter(t => INITIAL_DISPLAY_TOKENS.includes(t.symbol));
  const hasMoreTokens = displayTokens.length > INITIAL_DISPLAY_TOKENS.length;

  return (
    <div className="bg-transparent border border-border rounded-xl p-4 h-full flex flex-col overflow-hidden">
      <div className="border-b border-border pb-2 mb-2 flex-shrink-0">
        <div className="flex justify-between items-center">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-yellow-500">Token Prices</h3>
          <button className="text-[8px] text-muted-foreground hover:text-foreground uppercase px-2 py-0.5 border border-border rounded">
            24H Change
          </button>
        </div>
      </div>
      
      {/* Token list - shows 9 tokens or all */}
      <div className="flex-1 overflow-hidden">
        <div className="space-y-1">
          {visibleTokens.map((token) => {
            let priceData = prices[token.address];
            let price = priceData?.priceUsd || 0;
            let change = priceData?.priceChange24h || 0;
            
            if (isStablecoin(token.symbol)) {
              price = 1.000;
              change = 0;
            }
            
            const isSelected = selectedToken === token.symbol;

            return (
              <button
                key={token.id}
                onClick={() => onSelectToken(token)}
                className={cn(
                  "w-full flex items-center justify-between px-2 py-2 rounded-lg transition-colors",
                  isSelected ? "bg-transparent" : "hover:bg-muted/50"
                )}
              >
                <span className="text-[11px] font-bold text-foreground uppercase">{token.symbol}</span>
                <div className="flex items-center gap-3">
                  <span className="text-[11px] text-foreground font-mono">{formatPrice(token.symbol, price)}</span>
                  <span className={cn(
                    "text-[10px] font-medium w-16 text-right",
                    change >= 0 ? "text-green-600" : "text-red-600"
                  )}>
                    {formatChange(change)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      
      {/* "More Tokens" expander - directly under the 9 token cards */}
      {hasMoreTokens && (
        <button
          onClick={() => setShowAllTokens(!showAllTokens)}
          className="mt-1 pt-2 border-t border-border w-full flex items-center justify-center gap-1 text-[8px] text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
        >
          {showAllTokens ? (
            <>
              <ChevronUp className="w-3 h-3" />
              <span>Show Less</span>
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3" />
              <span>More Tokens ({displayTokens.length - INITIAL_DISPLAY_TOKENS.length})</span>
            </>
          )}
        </button>
      )}
    </div>
  );
};

// ============ TOKEN DROPDOWN ============
const TokenDropdown = React.forwardRef(({ value, onChange, isOpen, setIsOpen, getTokenBalance, getTokenUsdValue }: any, ref) => {
  return (
    <div className="relative" ref={ref}>
      <button 
        onClick={() => setIsOpen(!isOpen)} 
        className="bg-card border border-border rounded-lg px-3 py-2 text-[10px] font-medium uppercase flex items-center gap-1 min-w-[90px] justify-between hover:bg-muted transition h-9 text-foreground"
      >
        <span>{value}</span>
        <ChevronDown className="w-3 h-3 text-muted-foreground" />
      </button>
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-56 bg-card border border-border rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
          {TOKENS.map((token) => {
            const balance = getTokenBalance ? getTokenBalance(token.symbol) : 0;
            const usdValue = getTokenUsdValue ? getTokenUsdValue(token.symbol) : 0;
            const isNative = token.isNative;
            const isStable = token.isStable;
            return (
              <button 
                key={token.id} 
                onClick={() => { 
                  onChange(token.symbol); 
                  setIsOpen(false); 
                }} 
                className="w-full px-3 py-2 text-left hover:bg-muted transition flex items-center justify-between text-[10px]"
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium uppercase text-foreground">{token.symbol}</span>
                  {isNative && <span className="text-[6px] text-yellow-500 uppercase">★</span>}
                  {isStable && <span className="text-[6px] text-blue-500 uppercase">STABLE</span>}
                </div>
                <div className="text-right">
                  <p className="text-[8px] font-mono text-muted-foreground">{formatTokenAmount(balance, token.symbol)}</p>
                  <p className="text-[7px] text-muted-foreground">${usdValue.toFixed(2)}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
});
TokenDropdown.displayName = "TokenDropdown";

// ============ LIMIT ORDER CARD ============
const LimitOrderCard = ({
  tradeType, setTradeType,
  selectedToken, setSelectedToken,
  amount, setAmount,
  nativeAmount, setNativeAmount,
  limitPrice, setLimitPrice,
  swapLoading,
  currentPriceInUsdt,
  getTokenBalance, getTokenUsdValue,
  handleAmountChange, handleNativeAmountChange,
  handleCreateOrder,
  showTokenDropdown, setShowTokenDropdown,
  tokenDropdownRef,
  TokenDropdown,
  minimumAmount,
  isDesktop,
  nativeSymbol,
}: any) => {
  const cardClassName = isDesktop
    ? "bg-card border border-border rounded-xl p-6 h-full shadow-sm"
    : "bg-transparent p-0";

  const nativeBalance = getTokenBalance(nativeSymbol) || 0;
  const tokenBalance = getTokenBalance(selectedToken) || 0;

  const formatPriceDisplay = (price: number) => {
    if (!price || price === 0) return "0.000000";
    if (price < 0.000001) return price.toFixed(10);
    if (price < 0.00001) return price.toFixed(8);
    if (price < 0.001) return price.toFixed(6);
    if (price < 1) return price.toFixed(6);
    return price.toFixed(4);
  };

  return (
    <div className={cardClassName}>
      <div className="mb-4">
        <button
          className="text-[11px] font-bold uppercase tracking-wider text-yellow-500 hover:text-yellow-400 transition-colors cursor-pointer"
        >
          Limit Order Execution
        </button>
      </div>

      <div className="flex gap-2 mb-4">
        <button 
          onClick={() => setTradeType("buy")} 
          className={cn(
            "flex-1 py-2 rounded-lg text-[10px] font-medium uppercase transition border",
            tradeType === "buy" 
              ? "bg-yellow-500 text-white border-yellow-500" 
              : "bg-transparent text-muted-foreground border-border hover:bg-muted"
          )}
        >
          BUY LIMIT
        </button>
        <button 
          onClick={() => setTradeType("sell")} 
          className={cn(
            "flex-1 py-2 rounded-lg text-[10px] font-medium uppercase transition border",
            tradeType === "sell" 
              ? "bg-red-500 text-white border-red-500" 
              : "bg-transparent text-muted-foreground border-border hover:bg-muted"
          )}
        >
          SELL LIMIT
        </button>
      </div>

      <div className="space-y-1 mb-3">
        <div className="flex justify-between items-center">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">{tradeType === "buy" ? "BUY" : "SELL"} TOKEN</label>
          <span className="text-[8px] text-muted-foreground">Balance: {formatTokenAmount(tokenBalance, selectedToken)} {selectedToken}</span>
        </div>
        <div className="flex gap-2">
          <TokenDropdown 
            value={selectedToken} 
            onChange={setSelectedToken} 
            isOpen={showTokenDropdown} 
            setIsOpen={setShowTokenDropdown} 
            ref={tokenDropdownRef} 
            getTokenBalance={getTokenBalance} 
            getTokenUsdValue={getTokenUsdValue}
          />
          <Input 
            type="number" 
            placeholder="0.00" 
            value={amount} 
            onChange={(e) => handleAmountChange(e.target.value)} 
            className="flex-1 text-right text-[10px] bg-card border border-border text-foreground h-9 focus:outline-none focus:border-yellow-500" 
            disabled={swapLoading} 
          />
        </div>
      </div>

      <div className="space-y-1 mb-3">
        <div className="flex justify-between items-center">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">SOL AMOUNT</label>
          <span className="text-[8px] text-muted-foreground">Balance: {formatTokenAmount(nativeBalance, nativeSymbol)} {nativeSymbol}</span>
        </div>
        <Input 
          type="number" 
          placeholder="0.00" 
          value={nativeAmount} 
          onChange={(e) => handleNativeAmountChange(e.target.value)} 
          className="w-full text-right text-[10px] bg-card border border-border text-foreground h-9 focus:outline-none focus:border-yellow-500" 
          disabled={swapLoading} 
        />
      </div>

      <div className="space-y-1 mb-3">
        <div className="flex justify-between items-center">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">LIMIT PRICE</label>
          <span className="text-[8px] font-mono text-yellow-500">${formatPriceDisplay(currentPriceInUsdt)} USDT</span>
        </div>
        <Input 
          type="number" 
          placeholder="0.00" 
          value={limitPrice} 
          onChange={(e) => setLimitPrice(e.target.value)} 
          className="w-full text-[10px] bg-card border border-border text-foreground h-9 focus:outline-none focus:border-yellow-500" 
        />
      </div>

      {limitPrice && amount && (
        <div className="bg-muted p-2 rounded-lg space-y-0.5 mb-3 border border-border">
          <div className="flex justify-between text-[8px]">
            <span className="text-muted-foreground uppercase">YOU {tradeType === "buy" ? "PAY" : "RECEIVE"}</span>
            <span className="font-medium text-foreground">{tradeType === "buy" ? `${nativeAmount} ${nativeSymbol}` : `${amount} ${selectedToken}`}</span>
          </div>
          <div className="flex justify-between text-[8px]">
            <span className="text-muted-foreground uppercase">YOU {tradeType === "buy" ? "RECEIVE" : "PAY"}</span>
            <span className="font-medium text-foreground">{tradeType === "buy" ? `${amount} ${selectedToken}` : `${nativeAmount} ${nativeSymbol}`}</span>
          </div>
          <div className="flex justify-between text-[8px]">
            <span className="text-muted-foreground uppercase">LIMIT PRICE</span>
            <span className="font-medium text-yellow-500">1 {selectedToken} = ${limitPrice} USDT</span>
          </div>
          <div className="flex justify-between text-[8px]">
            <span className="text-muted-foreground uppercase">TARGET DIFF</span>
            <span className={cn("font-medium", parseFloat(limitPrice) > currentPriceInUsdt ? "text-green-500" : "text-red-500")}>
              {(((parseFloat(limitPrice) - currentPriceInUsdt) / currentPriceInUsdt) * 100).toFixed(2)}%
            </span>
          </div>
        </div>
      )}

      <Button 
        onClick={handleCreateOrder} 
        disabled={!amount || !nativeAmount || !limitPrice || swapLoading || parseFloat(amount) < minimumAmount} 
        className={cn("w-full h-9 text-[10px] font-bold uppercase", tradeType === "buy" ? "bg-yellow-500 text-white hover:bg-yellow-600" : "bg-red-500 text-white hover:bg-red-600")}
      >
        {swapLoading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> PROCESSING...</> : tradeType === "buy" ? `BUY ${selectedToken}` : `SELL ${selectedToken}`}
      </Button>
    </div>
  );
};

// ============ LIMIT ORDERS HISTORY ============
const LimitOrdersHistory = ({ orders, onCancel, onRemove }: { orders: LimitOrder[]; onCancel: (id: string) => void; onRemove: (id: string) => void }) => {
  const [showHistory, setShowHistory] = useState(false);
  if (orders.length === 0) return null;
  
  const pendingOrders = orders.filter(o => o.status === "pending");
  const executedOrders = orders.filter(o => o.status === "executed");
  const cancelledOrders = orders.filter(o => o.status === "cancelled");
  
  return (
    <div className="mt-4">
      <button onClick={() => setShowHistory(!showHistory)} className="w-full bg-card border border-border rounded-xl p-3 flex items-center justify-between hover:bg-muted transition shadow-sm">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-yellow-500" />
          <span className="text-[10px] font-bold uppercase text-foreground">ORDER HISTORY</span>
          <span className="text-[8px] text-muted-foreground">({orders.length} orders)</span>
        </div>
        <ChevronDown className={cn("w-4 h-4 text-muted-foreground transition-transform", showHistory && "rotate-180")} />
      </button>
      {showHistory && (
        <div className="bg-card border border-border rounded-xl p-4 mt-2 shadow-sm">
          <h3 className="text-[10px] font-bold flex items-center gap-2 mb-3 uppercase text-yellow-500">
            <Clock className="w-3 h-3" />YOUR LIMIT ORDERS
          </h3>
          
          {pendingOrders.length > 0 && (
            <div className="mb-3">
              <p className="text-[8px] font-bold text-yellow-500 mb-1 uppercase">PENDING</p>
              <div className="space-y-2">
                {pendingOrders.map((order) => (
                  <div key={order.id} className="bg-muted p-2 rounded-lg border border-border">
                    <div className="flex justify-between items-start mb-1">
                      <div>
                        <p className="text-[8px] font-mono font-bold uppercase text-muted-foreground">{order.id.slice(-8)}</p>
                        <p className="text-[9px] uppercase text-foreground">{order.orderType === "buy" ? "BUY" : "SELL"} {order.fromAmount.toFixed(6)} {order.fromToken}</p>
                        <p className="text-[7px] text-muted-foreground uppercase">@ ${order.limitPrice.toFixed(6)} USDT</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[6px] px-2 py-0.5 rounded-full uppercase bg-yellow-100 text-yellow-600">PENDING</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <p className="text-[6px] text-muted-foreground uppercase">CREATED: {new Date(order.createdAt).toLocaleString()}</p>
                      <button onClick={() => onCancel(order.id)} className="text-[6px] text-red-500 hover:text-red-600 uppercase">CANCEL</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {executedOrders.length > 0 && (
            <div className="mb-3">
              <p className="text-[8px] font-bold text-green-500 mb-1 uppercase">EXECUTED</p>
              <div className="space-y-2">
                {executedOrders.map((order) => (
                  <div key={order.id} className="bg-muted p-2 rounded-lg border border-border">
                    <div className="flex justify-between items-start mb-1">
                      <div>
                        <p className="text-[8px] font-mono font-bold uppercase text-muted-foreground">{order.id.slice(-8)}</p>
                        <p className="text-[9px] uppercase text-foreground">{order.orderType === "buy" ? "BUY" : "SELL"} {order.fromAmount.toFixed(6)} {order.fromToken}</p>
                        <p className="text-[7px] text-muted-foreground uppercase">@ ${order.limitPrice.toFixed(6)} USDT</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[6px] px-2 py-0.5 rounded-full uppercase bg-green-100 text-green-600">EXECUTED</span>
                        <button onClick={() => onRemove(order.id)} className="p-0.5 hover:bg-muted rounded transition text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <p className="text-[6px] text-muted-foreground uppercase">EXECUTED: {new Date(order.executedAt!).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {cancelledOrders.length > 0 && (
            <div>
              <p className="text-[8px] font-bold text-red-500 mb-1 uppercase">CANCELLED</p>
              <div className="space-y-2">
                {cancelledOrders.map((order) => (
                  <div key={order.id} className="bg-muted p-2 rounded-lg border border-border">
                    <div className="flex justify-between items-start mb-1">
                      <div>
                        <p className="text-[8px] font-mono font-bold uppercase text-muted-foreground">{order.id.slice(-8)}</p>
                        <p className="text-[9px] uppercase text-foreground">{order.orderType === "buy" ? "BUY" : "SELL"} {order.fromAmount.toFixed(6)} {order.fromToken}</p>
                        <p className="text-[7px] text-muted-foreground uppercase">@ ${order.limitPrice.toFixed(6)} USDT</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[6px] px-2 py-0.5 rounded-full uppercase bg-red-100 text-red-600">CANCELLED</span>
                        <button onClick={() => onRemove(order.id)} className="p-0.5 hover:bg-muted rounded transition text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <p className="text-[6px] text-muted-foreground uppercase">CANCELLED: {new Date(order.cancelledAt!).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============ MARQUEE COMPONENTS ============
const DesktopMarquee = ({ prices }: { prices: Record<string, { price: number; change: number }> }) => {
  const marqueeItems = DISPLAY_TOKENS_LIST.map(symbol => {
    let priceData = prices[symbol];
    let price = priceData?.price || 0;
    let change = priceData?.change || 0;
    
    if (isStablecoin(symbol)) {
      price = 1.000;
      change = 0;
    }
    
    return {
      symbol,
      price,
      change,
    };
  }).filter(item => item.price > 0);

  const loopItems = [...marqueeItems, ...marqueeItems];

  const formatPrice = (symbol: string, price: number) => {
    if (!price) return "$0.00";
    if (symbol === "SOL" || symbol === "WBTC" || symbol === "WETH") {
      return `$${price.toFixed(2)}`;
    }
    if (isStablecoin(symbol)) {
      return `$${price.toFixed(3)}`;
    }
    return `$${price.toFixed(6)}`;
  };

  if (marqueeItems.length === 0) return null;

  return (
    <div className="overflow-hidden whitespace-nowrap border-b border-border py-2">
      <div className="inline-block animate-marquee-desktop">
        {loopItems.map((item, idx) => (
          <div key={idx} className="inline-flex items-center mx-4 text-[10px] gap-2">
            <span className="text-muted-foreground uppercase">{item.symbol}</span>
            <span className="text-foreground font-semibold">{formatPrice(item.symbol, item.price)}</span>
            <span className={`font-semibold ${item.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {item.change >= 0 ? '+' : ''}{item.change.toFixed(2)}%
            </span>
          </div>
        ))}
      </div>
      <style>{`
        @keyframes marquee-desktop { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-marquee-desktop { animation: marquee-desktop 30s linear infinite; }
      `}</style>
    </div>
  );
};

const MobileMarquee = ({ prices }: { prices: Record<string, { price: number; change: number }> }) => {
  const marqueeItems = DISPLAY_TOKENS_LIST.map(symbol => {
    let priceData = prices[symbol];
    let price = priceData?.price || 0;
    let change = priceData?.change || 0;
    
    if (isStablecoin(symbol)) {
      price = 1.000;
      change = 0;
    }
    
    return {
      symbol,
      price,
      change,
    };
  }).filter(item => item.price > 0);

  const loopItems = [...marqueeItems, ...marqueeItems];

  const formatPrice = (symbol: string, price: number) => {
    if (!price) return "$0.00";
    if (symbol === "SOL" || symbol === "WBTC" || symbol === "WETH") {
      return `$${price.toFixed(2)}`;
    }
    if (isStablecoin(symbol)) {
      return `$${price.toFixed(3)}`;
    }
    return `$${price.toFixed(6)}`;
  };

  if (marqueeItems.length === 0) return null;

  return (
    <div className="overflow-hidden whitespace-nowrap flex-1">
      <div className="inline-block animate-marquee-mobile">
        {loopItems.map((item, idx) => (
          <div key={idx} className="inline-flex items-center mx-3 text-[10px] gap-1">
            <span className="text-muted-foreground uppercase">{item.symbol}</span>
            <span className="text-foreground font-semibold">{formatPrice(item.symbol, item.price)}</span>
            <span className={`font-semibold ${item.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {item.change >= 0 ? '+' : ''}{item.change.toFixed(2)}%
            </span>
          </div>
        ))}
      </div>
      <style>{`
        @keyframes marquee-mobile { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-marquee-mobile { animation: marquee-mobile 40s linear infinite; }
      `}</style>
    </div>
  );
};

// ============ NOTIFICATION ============
const SimpleNotification = ({ message, type, onClose }: { message: string; type: "success" | "error" | "info"; onClose: () => void }) => {
  useEffect(() => {
    const timer = setTimeout(() => onClose(), 3000);
    return () => clearTimeout(timer);
  }, [onClose]);
  return (
    <div className={cn("fixed top-20 right-4 z-[200] p-3 rounded-lg shadow-lg border animate-slide-in", type === "success" && "bg-green-500/90 border-green-400 text-white", type === "error" && "bg-red-500/90 border-red-400 text-white", type === "info" && "bg-blue-500/90 border-blue-400 text-white")}>
      <div className="flex items-center gap-2">
        {type === "success" && <CheckCircle className="w-4 h-4" />}
        {type === "error" && <AlertTriangle className="w-4 h-4" />}
        {type === "info" && <Info className="w-4 h-4" />}
        <p className="text-[10px] font-medium uppercase">{message}</p>
        <button onClick={onClose} className="ml-2 hover:opacity-70"><X className="w-3 h-3" /></button>
      </div>
    </div>
  );
};

// ============ BACKUP FILE DIALOG ============
const BackupFileDialog = ({ show, onClose, onSubmit, error, loading = false }) => {
  const [manualBackupText, setManualBackupText] = useState("");
  const [manualError, setManualError] = useState("");

  if (!show) return null;

  const validateBackupContent = (content: string): boolean => {
    if (content.length > 1024 * 1024) throw new Error("File too large. Maximum size is 1MB.");
    const parsed = JSON.parse(content);
    if (!parsed.magic || !parsed.salt || !parsed.iv || !parsed.data) throw new Error("Invalid backup file structure.");
    if (parsed.magic !== "FIXORIUM-MPC") throw new Error("Invalid backup file format.");
    return true;
  };

  const handleManualSubmit = () => {
    if (!manualBackupText.trim()) {
      setManualError("Please paste your backup JSON");
      return;
    }
    try {
      validateBackupContent(manualBackupText);
      onSubmit(manualBackupText);
      setManualBackupText("");
      setManualError("");
    } catch (err: any) {
      setManualError(err.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card border border-border rounded-lg p-6 max-w-sm w-full mx-4 shadow-lg">
        {(error || manualError) && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-600">{error || manualError}</div>}
        
        <div className="space-y-3">
          <textarea
            value={manualBackupText}
            onChange={(e) => setManualBackupText(e.target.value)}
            placeholder='Paste your backup JSON here...'
            className="w-full h-32 bg-muted border border-border rounded-lg p-2 text-[8px] font-mono text-foreground resize-none focus:outline-none focus:border-yellow-500"
          />
          <Button onClick={handleManualSubmit} disabled={loading} className="w-full bg-yellow-500 text-white hover:bg-yellow-600 text-sm">
            Verify & Use
          </Button>
        </div>

        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="flex-1 border-border text-foreground hover:bg-muted" disabled={loading}>Cancel</Button>
        </div>
      </div>
    </div>
  );
};

// ============ PASSWORD DIALOG ============
const PasswordDialog = ({ show, onClose, onSubmit, error, loading }: any) => {
  const [password, setPassword] = useState("");
  if (!show) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-sm w-full p-5 shadow-xl">
        <h3 className="text-[10px] font-bold uppercase mb-3 text-yellow-500">ENTER PASSWORD</h3>
        <p className="text-[8px] text-muted-foreground mb-4 uppercase">PASSWORD REQUIRED TO COMPLETE ORDER</p>
        {error && <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-[8px] text-red-600 uppercase">{error}</div>}
        <input 
          type="password" 
          value={password} 
          onChange={(e) => setPassword(e.target.value)} 
          placeholder="ENTER PASSWORD" 
          className="w-full bg-muted border border-border rounded-md px-3 py-2 text-[10px] text-foreground uppercase mb-4 focus:outline-none focus:border-yellow-500"
          onKeyPress={(e) => e.key === "Enter" && onSubmit(password)} 
          autoFocus 
          disabled={loading}
        />
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1 text-[10px] uppercase border-border text-foreground hover:bg-muted h-9" disabled={loading}>CANCEL</Button>
          <Button onClick={() => onSubmit(password)} disabled={!password.trim() || loading} className="flex-1 bg-yellow-500 text-white hover:bg-yellow-600 text-[10px] uppercase font-bold h-9">{loading ? "PROCESSING..." : "CONFIRM"}</Button>
        </div>
      </div>
    </div>
  );
};

// ============ MAIN COMPONENT ============
export default function LimitOrder() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [tokenBalances, setTokenBalances] = useState<any[]>([]);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [tradeType, setTradeType] = useState<"buy" | "sell">("buy");
  const [selectedToken, setSelectedToken] = useState<string>("SOL");
  const [amount, setAmount] = useState<string>("");
  const [nativeAmount, setNativeAmount] = useState<string>("");
  const [limitPrice, setLimitPrice] = useState<string>("");
  const [swapLoading, setSwapLoading] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState({ open: false, password: "", showPassword: false });
  const [showBackupDialog, setShowBackupDialog] = useState(false);
  const [backupFileContent, setBackupFileContent] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: "success" | "error" | "info" } | null>(null);
  const [limitOrders, setLimitOrders] = useState<LimitOrder[]>([]);
  const [showTokenDropdown, setShowTokenDropdown] = useState(false);
  const tokenDropdownRef = useRef<HTMLDivElement>(null);

  const [refreshTrigger, setRefreshTrigger] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshTrigger(prev => prev + 1);
      checkAndExecuteOrders();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const tokenAddressesList = useMemo(() => TOKENS.map(token => token.address), []);
  const { prices: dexPrices, loading: pricesLoading } = useMultipleTokenPrices(
    tokenAddressesList, 
    { refetchInterval: 30000 }
  );

  const displayPrices = useMemo(() => {
    const result: Record<string, { priceUsd: number; priceChange24h: number }> = {};
    TOKENS.forEach(token => {
      let priceData = dexPrices[token.address];
      let price = priceData?.priceUsd || 0;
      let change = priceData?.priceChange24h || 0;
      
      if (isStablecoin(token.symbol)) {
        price = 1.000;
        change = 0;
      } else if (price > 1000000 || price < 0) {
        price = 0;
      }
      
      result[token.address] = {
        priceUsd: price,
        priceChange24h: change,
      };
    });
    return result;
  }, [dexPrices]);

  const marqueePrices = useMemo(() => {
    const result: Record<string, { price: number; change: number }> = {};
    TOKENS.forEach(token => {
      let priceData = dexPrices[token.address];
      let price = priceData?.priceUsd || 0;
      let change = priceData?.priceChange24h || 0;
      
      if (isStablecoin(token.symbol)) {
        price = 1.000;
        change = 0;
      } else if (price <= 0) {
        price = FALLBACK_PRICES[token.symbol] || 0;
        change = 0;
      }
      
      result[token.symbol] = { price, change };
    });
    return result;
  }, [dexPrices]);

  // Get token price in USDT (USD value)
  const tokenPricesInUsdt = useMemo(() => {
    const mappedPrices: Record<string, number> = {};
    TOKENS.forEach(token => {
      if (isStablecoin(token.symbol)) {
        mappedPrices[token.symbol] = 1.000;
        return;
      }
      
      const address = token.address;
      const dexPrice = dexPrices[address]?.priceUsd;
      
      if (dexPrice && dexPrice > 0 && !isNaN(dexPrice)) {
        mappedPrices[token.symbol] = dexPrice;
      } else {
        mappedPrices[token.symbol] = 0;
      }
    });
    return mappedPrices;
  }, [dexPrices]);

  const nativeSymbol = "SOL";

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch("/api/auth", { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include" });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.user) {
            setUser(data.user);
            loadLimitOrders(data.user.email);
            setLoading(false);
            return;
          }
        }
        navigate("/login");
      } catch (error) { console.error("[LimitOrder] Auth error:", error); navigate("/login"); } finally { setLoading(false); }
    };
    fetchUser();
  }, [navigate]);

  const loadLimitOrders = (userEmail: string) => {
    try { 
      const storedOrders = localStorage.getItem(`limit_orders_${userEmail}`); 
      if (storedOrders) {
        const orders = JSON.parse(storedOrders);
        setLimitOrders(orders);
        setTimeout(() => checkAndExecuteOrders(orders), 1000);
      }
    } catch (error) { console.error(error); }
  };

  const saveLimitOrders = (orders: LimitOrder[]) => { 
    if (user?.email) localStorage.setItem(`limit_orders_${user.email}`, JSON.stringify(orders)); 
  };

  const checkAndExecuteOrders = useCallback((orders?: LimitOrder[]) => {
    const currentOrders = orders || limitOrders;
    const pendingOrders = currentOrders.filter(o => o.status === "pending");
    
    if (pendingOrders.length === 0) return;

    let updated = false;
    const updatedOrders = currentOrders.map(order => {
      if (order.status !== "pending") return order;
      
      const currentPrice = tokenPricesInUsdt[order.toToken] || 0;
      if (currentPrice === 0) return order;
      
      const priceReached = order.orderType === "buy" 
        ? currentPrice <= order.limitPrice 
        : currentPrice >= order.limitPrice;
      
      if (priceReached) {
        updated = true;
        return {
          ...order,
          status: "executed" as const,
          executedAt: Date.now(),
        };
      }
      return order;
    });

    if (updated) {
      setLimitOrders(updatedOrders);
      saveLimitOrders(updatedOrders);
      toast({ 
        title: "🎯 ORDER EXECUTED!", 
        description: `${updatedOrders.filter(o => o.status === "executed").length} order(s) executed at target price` 
      });
    }
  }, [limitOrders, tokenPricesInUsdt]);

  const solanaWalletAddress = user?.wallets?.solana || null;
  const { balances: solanaBalances, refetch: refetchBalances } = useSolanaBalances({ 
    walletAddress: solanaWalletAddress || "", 
    refetchInterval: false, 
    enabled: !!solanaWalletAddress 
  });

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const balances: any[] = [];
    for (const token of TOKENS) {
      const balance = solanaBalances?.[token.symbol] || 0;
      const price = tokenPricesInUsdt[token.symbol] || 0;
      balances.push({ symbol: token.symbol, balance, usdValue: balance * price, chain: token.chain });
    }
    if (balances.length > 0) setTokenBalances(balances);
  }, [solanaBalances, tokenPricesInUsdt]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tokenDropdownRef.current && !tokenDropdownRef.current.contains(event.target as Node)) setShowTokenDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getTokenBalance = (symbol: string): number => { 
    const token = tokenBalances.find(t => t.symbol === symbol); 
    return token?.balance || 0; 
  };
  
  const getTokenUsdValue = (symbol: string): number => { 
    const token = tokenBalances.find(t => t.symbol === symbol);
    return token?.usdValue || 0; 
  };
  
  const currentPriceInUsdt = tokenPricesInUsdt[selectedToken] || 0;
  const minimumAmountForToken = getMinimumAmount(selectedToken);

  const handleAmountChange = (value: string) => {
    setAmount(value);
    if (value && limitPrice) {
      const solPrice = dexPrices[TOKENS.find(t => t.symbol === "SOL")?.address || ""]?.priceUsd || 180;
      const calculatedNative = (parseFloat(value) * parseFloat(limitPrice)) / solPrice;
      setNativeAmount(calculatedNative.toFixed(6));
    } else { 
      setNativeAmount(""); 
    }
  };

  const handleNativeAmountChange = (value: string) => {
    setNativeAmount(value);
    if (value && limitPrice) {
      const solPrice = dexPrices[TOKENS.find(t => t.symbol === "SOL")?.address || ""]?.priceUsd || 180;
      const calculatedAmount = (parseFloat(value) * solPrice) / parseFloat(limitPrice);
      setAmount(calculatedAmount.toFixed(6));
    } else { 
      setAmount(""); 
    }
  };

  const createLimitOrder = async (password: string, backupFile?: string) => {
    if (!amount || !limitPrice) { 
      setNotification({ message: "Please fill all fields", type: "error" }); 
      return; 
    }
    
    const token = TOKENS.find(t => t.symbol === selectedToken);
    if (!token) { 
      setNotification({ message: "Invalid token", type: "error" }); 
      return; 
    }

    if (parseFloat(amount) < minimumAmountForToken) { 
      setNotification({ message: `Minimum amount is ${minimumAmountForToken.toFixed(6)} ${selectedToken}`, type: "error" }); 
      return; 
    }

    if (tradeType === "sell") {
      const availableBalance = getTokenBalance(selectedToken);
      if (availableBalance < parseFloat(amount)) {
        setNotification({ 
          message: `Insufficient balance. You have ${formatTokenAmount(availableBalance, selectedToken)} ${selectedToken}`, 
          type: "error" 
        });
        return;
      }
    }
    
    setSwapLoading(true);
    try {
      const fromToken = tradeType === "buy" ? nativeSymbol : selectedToken;
      const toToken = tradeType === "buy" ? selectedToken : nativeSymbol;
      let fromAmount = tradeType === "buy" ? parseFloat(nativeAmount) : parseFloat(amount);
      
      const newOrder: LimitOrder = {
        id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        userId: user?.email || "",
        fromToken,
        toToken,
        fromAmount,
        toAmount: parseFloat(tradeType === "buy" ? amount : nativeAmount),
        limitPrice: parseFloat(limitPrice),
        orderType: tradeType,
        status: "pending",
        createdAt: Date.now(),
        chain: token.chain,
      };
      
      const updatedOrders = [...limitOrders, newOrder];
      setLimitOrders(updatedOrders);
      saveLimitOrders(updatedOrders);

      setNotification({ message: "✅ LIMIT ORDER CREATED! Will auto-execute when price target is reached.", type: "success" });
      setAmount(""); 
      setNativeAmount(""); 
      setLimitPrice("");
      setPasswordDialog({ open: false, password: "", showPassword: false });
    } catch (error: any) { 
      setNotification({ message: error.message || "Failed to create order", type: "error" }); 
    } finally { 
      setSwapLoading(false); 
    }
  };

  const handleCreateOrder = async (password?: string, backupFile?: string) => {
    if (!user) { 
      setNotification({ message: "User not found", type: "error" }); 
      return; 
    }

    if (!password) {
      if (user.isMPC && !backupFileContent) {
        setShowBackupDialog(true);
        return;
      }
      setPasswordDialog({ ...passwordDialog, open: true });
      return;
    }
    await createLimitOrder(password, backupFile);
  };

  const handleBackupSubmit = (backupContent: string) => {
    setBackupFileContent(backupContent);
    setShowBackupDialog(false);
    setPasswordDialog({ ...passwordDialog, open: true });
  };

  const cancelLimitOrder = (orderId: string) => { 
    const updatedOrders = limitOrders.map(o => 
      o.id === orderId ? { ...o, status: "cancelled" as const, cancelledAt: Date.now() } : o
    ); 
    setLimitOrders(updatedOrders); 
    saveLimitOrders(updatedOrders); 
    setNotification({ message: "Order cancelled", type: "info" }); 
  };

  const removeOrderFromHistory = (orderId: string) => { 
    const updatedOrders = limitOrders.filter(o => o.id !== orderId); 
    setLimitOrders(updatedOrders); 
    saveLimitOrders(updatedOrders); 
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-yellow-500" /></div>;

  if (isDesktop) {
    return (
      <div className="desktop-dark min-h-screen bg-background text-foreground px-4 pb-4 pt-0">
        {/* Desktop-only dark black theme override — layout untouched */}
        <style>{`
          .desktop-dark {
            --background: 0 0% 0%;
            --card: 240 10% 4%;
            --popover: 240 10% 4%;
            --border: 240 4% 16%;
            --input: 240 4% 16%;
            --muted: 240 4% 12%;
            --foreground: 0 0% 98%;
            --muted-foreground: 240 5% 65%;
          }
        `}</style>
        <DesktopMarquee prices={marqueePrices} />
        <div className="flex gap-2 h-[calc(100vh-2rem)] mt-4">
          <div className="w-[25%]">
            <TokenPriceList 
              prices={displayPrices}
              onSelectToken={(token: any) => {
                setSelectedToken(token.symbol);
                setAmount("");
                setNativeAmount("");
                setLimitPrice("");
              }}
              selectedToken={selectedToken}
            />
          </div>
          
          <div className="w-[50%]">
            <BirdeyeChart 
              tokenAddress={TOKENS.find(t => t.symbol === selectedToken)?.address || TOKENS[0].address} 
              chain="solana"
            />
          </div>
          
          <div className="w-[25%]">
            <LimitOrderCard
              tradeType={tradeType} setTradeType={setTradeType}
              selectedToken={selectedToken} setSelectedToken={setSelectedToken}
              amount={amount} setAmount={setAmount}
              nativeAmount={nativeAmount} setNativeAmount={setNativeAmount}
              limitPrice={limitPrice} setLimitPrice={setLimitPrice}
              swapLoading={swapLoading}
              currentPriceInUsdt={currentPriceInUsdt}
              getTokenBalance={getTokenBalance}
              getTokenUsdValue={getTokenUsdValue}
              handleAmountChange={handleAmountChange}
              handleNativeAmountChange={handleNativeAmountChange}
              handleCreateOrder={() => handleCreateOrder()}
              showTokenDropdown={showTokenDropdown}
              setShowTokenDropdown={setShowTokenDropdown}
              tokenDropdownRef={tokenDropdownRef}
              TokenDropdown={TokenDropdown}
              minimumAmount={minimumAmountForToken}
              isDesktop={true}
              nativeSymbol={nativeSymbol}
            />
          </div>
        </div>
        <LimitOrdersHistory orders={limitOrders} onCancel={cancelLimitOrder} onRemove={removeOrderFromHistory} />
        {notification && <SimpleNotification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />}
        {passwordDialog.open && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card border border-border rounded-xl p-4 w-full max-w-sm space-y-3 shadow-lg">
              <h2 className="text-base font-bold uppercase text-foreground">CONFIRM LIMIT ORDER</h2>
              <p className="text-[10px] text-muted-foreground uppercase">ENTER PASSWORD TO CREATE ORDER</p>
              <div className="space-y-1">
                <label className="text-[10px] font-medium uppercase text-muted-foreground">PASSWORD</label>
                <div className="relative">
                  <Input 
                    type={passwordDialog.showPassword ? "text" : "password"} 
                    placeholder="ENTER PASSWORD" 
                    value={passwordDialog.password} 
                    onChange={(e) => setPasswordDialog({ ...passwordDialog, password: e.target.value })} 
                    disabled={swapLoading} 
                    onKeyPress={(e) => e.key === "Enter" && handleCreateOrder(passwordDialog.password, backupFileContent || undefined)} 
                    className="uppercase text-xs h-8 bg-card border border-border text-foreground focus:outline-none focus:border-yellow-500" 
                  />
                  <button 
                    onClick={() => setPasswordDialog({ ...passwordDialog, showPassword: !passwordDialog.showPassword })} 
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-[8px] uppercase text-muted-foreground"
                  >
                    SHOW
                  </button>
                </div>
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={() => setPasswordDialog({ open: false, password: "", showPassword: false })} 
                  disabled={swapLoading} 
                  variant="outline" 
                  className="flex-1 text-xs border-border text-foreground hover:bg-muted"
                >
                  CANCEL
                </Button>
                <Button 
                  onClick={() => handleCreateOrder(passwordDialog.password, backupFileContent || undefined)} 
                  disabled={!passwordDialog.password || swapLoading} 
                  className={cn("flex-1 text-xs", tradeType === "buy" ? "bg-yellow-500 text-white" : "bg-red-500 text-white")}
                >
                  {swapLoading ? "PROCESSING..." : "CREATE ORDER"}
                </Button>
              </div>
            </div>
          </div>
        )}
        <BackupFileDialog show={showBackupDialog} onClose={() => setShowBackupDialog(false)} onSubmit={handleBackupSubmit} error="" loading={false} />
      </div>
    );
  }

  // Mobile view
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="border-b border-border bg-transparent py-1">
        <MobileMarquee prices={marqueePrices} />
      </div>
      <div className="p-4">
        <LimitOrderCard
          tradeType={tradeType} setTradeType={setTradeType}
          selectedToken={selectedToken} setSelectedToken={setSelectedToken}
          amount={amount} setAmount={setAmount}
          nativeAmount={nativeAmount} setNativeAmount={setNativeAmount}
          limitPrice={limitPrice} setLimitPrice={setLimitPrice}
          swapLoading={swapLoading}
          currentPriceInUsdt={currentPriceInUsdt}
          getTokenBalance={getTokenBalance}
          getTokenUsdValue={getTokenUsdValue}
          handleAmountChange={handleAmountChange}
          handleNativeAmountChange={handleNativeAmountChange}
          handleCreateOrder={() => handleCreateOrder()}
          showTokenDropdown={showTokenDropdown}
          setShowTokenDropdown={setShowTokenDropdown}
          tokenDropdownRef={tokenDropdownRef}
          TokenDropdown={TokenDropdown}
          minimumAmount={minimumAmountForToken}
          isDesktop={false}
          nativeSymbol={nativeSymbol}
        />
      </div>
      <LimitOrdersHistory orders={limitOrders} onCancel={cancelLimitOrder} onRemove={removeOrderFromHistory} />
      {notification && <SimpleNotification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />}
      {passwordDialog.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl p-4 w-full max-w-sm space-y-3 shadow-lg">
            <h2 className="text-base font-bold uppercase text-foreground">CONFIRM LIMIT ORDER</h2>
            <p className="text-[10px] text-muted-foreground uppercase">ENTER PASSWORD TO CREATE ORDER</p>
            <div className="space-y-1">
              <label className="text-[10px] font-medium uppercase text-muted-foreground">PASSWORD</label>
              <div className="relative">
                <Input 
                  type={passwordDialog.showPassword ? "text" : "password"} 
                  placeholder="ENTER PASSWORD" 
                  value={passwordDialog.password} 
                  onChange={(e) => setPasswordDialog({ ...passwordDialog, password: e.target.value })} 
                  disabled={swapLoading} 
                  onKeyPress={(e) => e.key === "Enter" && handleCreateOrder(passwordDialog.password, backupFileContent || undefined)} 
                  className="uppercase text-xs h-8 bg-card border border-border text-foreground focus:outline-none focus:border-yellow-500" 
                />
                <button 
                  onClick={() => setPasswordDialog({ ...passwordDialog, showPassword: !passwordDialog.showPassword })} 
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-[8px] uppercase text-muted-foreground"
                >
                  SHOW
                </button>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={() => setPasswordDialog({ open: false, password: "", showPassword: false })} 
                disabled={swapLoading} 
                variant="outline" 
                className="flex-1 text-xs border-border text-foreground hover:bg-muted"
              >
                CANCEL
              </Button>
              <Button 
                onClick={() => handleCreateOrder(passwordDialog.password, backupFileContent || undefined)} 
                disabled={!passwordDialog.password || swapLoading} 
                className={cn("flex-1 text-xs", tradeType === "buy" ? "bg-yellow-500 text-white" : "bg-red-500 text-white")}
              >
                {swapLoading ? "PROCESSING..." : "CREATE ORDER"}
              </Button>
            </div>
          </div>
        </div>
      )}
      <BackupFileDialog show={showBackupDialog} onClose={() => setShowBackupDialog(false)} onSubmit={handleBackupSubmit} error="" loading={false} />
    </div>
  );
}
